import React from "react";
import styled from "styled-components";
import { useProject } from "../../../hook/Subscription/useProject";
import PlanSummary from "./PlanSummary";
import FeaturesList from "./FeaturesList";
import PromoBox from "./PromoBox";
import ReferralBox from "./ReferralBox";
import HorizontalStepper from "./HorizontalStepper";

/**
 * Feature flag — backend support for promo codes and referral keys
 * isn't deployed yet (validation endpoints exist as stubs but the
 * pricing/contract logic that consumes the discount isn't wired). We
 * hide the input UI for now so users don't waste time entering codes
 * we can't honor; the state plumbing in SubscriptionFlow stays
 * intact, so flipping these flags to true is enough to re-enable the
 * full flow.
 *
 * TODO(Phase 3): flip to true once:
 *   - backend /subscription/promo/validate returns real records
 *   - createSubscription applies the discount to ContractAgreement.price
 *   - referral attribution writes to ReferralKey usage table
 */
const SHOW_PROMO    = false;
const SHOW_REFERRAL = false;

/**
 * Sidebar — left rail of the subscription wizard.
 *
 * Layout: flex column, height = 100% of parent.
 *   ┌─────────────────────┐
 *   │ Header              │ ← fixed
 *   │ PlanSummary         │ ← fixed
 *   │ FeaturesList ▼      │ ← flex-grow, scrolls inside
 *   │ PromoBox            │ ← fixed (hidden behind SHOW_PROMO)
 *   │ ReferralBox         │ ← fixed (hidden behind SHOW_REFERRAL)
 *   │ HorizontalStepper   │ ← fixed
 *   └─────────────────────┘
 */
function Sidebar({
  id_project,
  selectedPlan,
  steps = [],
  currentStep,
  initialPromo = "",
  initialRef = "",
  onPromoChange,
  onReferralChange,
  lang = "th",
}) {
  const { data: project, isLoading: projectLoading } = useProject({
    id_project,
  });

  return (
    <Wrapper>
      <Header>
        {projectLoading ? (
          <HeaderSkeleton />
        ) : project ? (
          <>
            <Logo>
              {project.logo_icon?.url ? (
                <img
                  src={project.logo_icon.url}
                  alt={project.name_project}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                project.project_acronym?.[0] ?? "?"
              )}
            </Logo>
            <HeaderText>
              <ProjectName>{project.name_project}</ProjectName>
              <ProjectSub>by PKINDEV</ProjectSub>
            </HeaderText>
          </>
        ) : null}
      </Header>

      <PlanSummary plan={selectedPlan} lang={lang} />

      <FeaturesList
        features={selectedPlan?.subscription_plan_detail}
        lang={lang}
      />

      {SHOW_PROMO && (
        <PromoBox
          initialCode={initialPromo}
          id_project={id_project}
          onChange={onPromoChange}
          lang={lang}
        />
      )}

      {SHOW_REFERRAL && (
        <ReferralBox
          initialCode={initialRef}
          onChange={onReferralChange}
          lang={lang}
        />
      )}

      <StepperBlock>
        <HorizontalStepper
          steps={steps}
          currentStep={currentStep}
          lang={lang}
        />
      </StepperBlock>
    </Wrapper>
  );
}

export default Sidebar;

// ─── Styled ────────────────────────────────────────────────

const Wrapper = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f1f3f5;
`;

const HeaderSkeleton = styled.div`
  width: 100%;
  height: 36px;
  background: #f1f3f5;
  border-radius: 6px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const Logo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #eef2ff;
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  margin-right: 10px;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1;
`;

const ProjectName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProjectSub = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const StepperBlock = styled.div`
  border-top: 1px solid #f1f3f5;
`;